// IdCardComponents.tsx
// Pure card rendering components + print HTML builder

import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import 'primeicons/primeicons.css';

// ─── Constants ───────────────────────────────────────────────────────────────
export const navy = '#0e225d';
export const gold  = '#fbb21f';

// Screen preview size — generously scaled for readability
export const CARD_W = 540;
export const CARD_H = 310;

// ─── Types ───────────────────────────────────────────────────────────────────
export interface BatchInfo {
  batch_id:     string;
  batch_name:   string;
  batch_time:   string;
  course_name:  string | null;
  course_title: string | null;
  is_active:    boolean;
  valid_till?:  string | null;
}

export interface Student {
  student_id:             string;
  enrollment_id:          string;
  name:                   string;
  dateOfBirth:            string | null;
  gender:                 string | null;
  mobileNumber:           string | null;
  fatherName:             string | null;
  motherName:             string | null;
  district:               string | null;
  state:                  string | null;
  villageTown:            string | null;
  profilePicture:         { cdn_url?: string } | null;
  status:                 string;
  date_of_admission:      string;
  is_registered:          boolean;
  category:               string | null;
  bloodGroup:             string | null;
  emergencyContactName?:  string | null;
  emergencyContactPhone?: string | null;
  validTill?:             string | null;
  student_custom_id?:     string | null;
}

export interface CardProps {
  student:       Student;
  batchInfo:     BatchInfo | null;
  schoolName:    string;
  schoolAddress: string;
  websiteUrl:    string;
  validTill?:    string | null;
  branchId?:     string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const parseDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (raw: string | null | undefined): string => {
  const d = parseDate(raw);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDOB = formatDate;

// ─── Subtle diagonal stripe pattern ──────────────────────────────────────────
const DiagPattern: React.FC<{ id: string; light?: boolean }> = ({ id, light }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: light ? 0.5 : 0.4 }}>
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="20"
          stroke={light ? 'rgba(14,34,93,0.07)' : 'rgba(251,178,31,0.14)'}
          strokeWidth="1.2" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

// ─── Watermark arc ────────────────────────────────────────────────────────────
const WatermarkArc: React.FC = () => (
  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', right: -40, bottom: -40, pointerEvents: 'none', opacity: 0.045 }}>
    <circle cx="110" cy="110" r="90" fill="none" stroke="#0e225d" strokeWidth="28" />
    <circle cx="110" cy="110" r="54" fill="none" stroke="#0e225d" strokeWidth="10" />
  </svg>
);

// ─── Dot-grid texture ─────────────────────────────────────────────────────────
const DotGrid: React.FC<{ id: string }> = ({ id }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 1 }}>
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="14" height="14">
        <circle cx="1.5" cy="1.5" r="1" fill="rgba(14,34,93,0.055)" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

// ─── Shared card shell ────────────────────────────────────────────────────────
const cardShell: React.CSSProperties = {
  width:         CARD_W,
  height:        CARD_H,
  borderRadius:  18,
  overflow:      'hidden',
  boxShadow:     '0 20px 60px rgba(14,34,93,0.22), 0 4px 16px rgba(14,34,93,0.12)',
  fontFamily:    "'Plus Jakarta Sans', sans-serif",
  display:       'flex',
  flexDirection: 'column',
  background:    '#fff',
  flexShrink:    0,
};

// ─── Shared header band ───────────────────────────────────────────────────────
const CardHeader: React.FC<{
  patId:         string;
  schoolName:    string;
  schoolAddress: string; // branch name only
}> = ({ patId, schoolName, schoolAddress }) => (
  <div style={{
    background: 'linear-gradient(100deg, #071640 0%, #0e225d 50%, #1a3a7a 100%)',
    padding: '11px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
  }}>
    <DiagPattern id={patId} />
    {/* Gold top stripe */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3.5, background: `linear-gradient(90deg, ${gold}, #ffdc71, ${gold})`, zIndex: 3 }} />

    <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 3 }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
      }}>
        <img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" alt="Logo"
          style={{ width: '84%', height: '84%', objectFit: 'contain' }} />
      </div>
      <div>
        {/* Institute name */}
        <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.2px' }}>
          {schoolName}
        </div>
        {/* Branch name only */}
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.82)', marginTop: 2 }}>
          {schoolAddress}
        </div>
      </div>
    </div>
  </div>
);

// ─── FRONT CARD ───────────────────────────────────────────────────────────────
export const IdCardFront: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill, branchId,
}) => {
  const resolvedValidTill = validTill ?? student.validTill ?? batchInfo?.valid_till ?? null;
  const pid = `fp-${student.student_id}`;
  const enrollmentId = student.student_custom_id || student.enrollment_id || student.student_id.slice(-6).toUpperCase();

  const infoRows: [string, string][] = [
    ['Student ID', enrollmentId],
    ['Father',     student.fatherName || '—'],
    ['Mother',     student.motherName || '—'],
    ['DOB',        formatDOB(student.dateOfBirth)],
    ['Mobile',     student.mobileNumber || '—'],
    ['Admitted',   formatDate(student.date_of_admission)],
    ['Valid Till', formatDate(resolvedValidTill)],
    ...(student.gender   ? [['Gender',   student.gender.charAt(0).toUpperCase() + student.gender.slice(1)] as [string, string]] : []),
    ...(student.category ? [['Category', student.category] as [string, string]] : []),
  ];

  return (
    <div style={cardShell}>
      <CardHeader patId={pid} schoolName={schoolName} schoolAddress={schoolAddress} />

      {/* ── BODY ── */}
      <div style={{
        flex: 1,
        display: 'flex',
        background: 'linear-gradient(150deg, #ffffff 0%, #eef2fb 100%)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <WatermarkArc />

        {/* ── Left: photo column ── */}
        <div style={{
          width: 150,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '14px 12px 12px',
          position: 'relative',
          background: 'linear-gradient(180deg, #f0f4ff 0%, #e6ecf8 100%)',
          borderRight: `1px solid rgba(14,34,93,0.1)`,
        }}>
          <DiagPattern id={`col-${pid}`} light />
          <div style={{ position: 'absolute', top: 0, right: 0, width: 3, height: '100%', background: `linear-gradient(180deg, transparent, ${gold}, transparent)`, zIndex: 2 }} />

          {/* Photo */}
          <div style={{
            width: 96, height: 108, borderRadius: 12, overflow: 'hidden',
            border: `3px solid ${gold}`,
            boxShadow: '0 6px 24px rgba(14,34,93,0.18)',
            background: gold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, position: 'relative', zIndex: 3,
          }}>
            {student.profilePicture?.cdn_url
              ? <img src={student.profilePicture.cdn_url} alt={student.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
              : <span style={{ fontSize: 34, fontWeight: 800, color: navy, letterSpacing: '-1px', userSelect: 'none' }}>
                  {getInitials(student.name)}
                </span>
            }
            {student.is_registered && (
              <div style={{
                position: 'absolute', bottom: 5, right: 5,
                width: 16, height: 16, borderRadius: '50%',
                background: '#16a34a', border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="pi pi-check" style={{ fontSize: 7, color: '#fff' }} />
              </div>
            )}
          </div>

          <div style={{
            fontSize: 11, fontWeight: 800, color: navy,
            textAlign: 'center', lineHeight: 1.25,
            wordBreak: 'break-word', width: '100%',
            position: 'relative', zIndex: 3,
          }}>
            {student.name}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'center', position: 'relative', zIndex: 3 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 20,
              background: 'rgba(14,34,93,0.08)', border: `1px solid rgba(14,34,93,0.15)`,
            }}>
              <i className="pi pi-graduation-cap" style={{ fontSize: 7, color: navy, opacity: 0.8 }} />
              <span style={{ fontSize: 8, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Student</span>
            </div>
            {student.bloodGroup && (
              <div style={{
                padding: '3px 10px', borderRadius: 20,
                background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
                fontSize: 8, fontWeight: 800, color: '#dc2626',
              }}>
                {student.bloodGroup}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: info column ── */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          padding: '14px 16px 12px', minWidth: 0, position: 'relative',
        }}>
          <DotGrid id={`dg-${pid}`} />
          <div style={{ position: 'absolute', top: 0, left: 0, width: 40, height: 3, background: gold, borderRadius: '0 0 3px 0' }} />

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '0px 14px', flex: 1, alignContent: 'space-evenly',
            position: 'relative', zIndex: 1,
          }}>
            {infoRows.map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 8, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 1 }}>
                  {label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {val}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gold bottom bar */}
      <div style={{ height: 3.5, background: `linear-gradient(90deg, ${navy}, ${gold}, ${navy})`, flexShrink: 0 }} />
    </div>
  );
};

// ─── BACK CARD ────────────────────────────────────────────────────────────────
export const IdCardBack: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill, branchId,
}) => {
  const qrRef = useRef<HTMLCanvasElement>(null);
  const pid   = `bp-${student.student_id}`;

  const qrUrl = branchId
    ? `http://hciindia.in/details/${branchId}`
    : `https://${websiteUrl}`;

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, qrUrl, {
        width: 82, margin: 1, color: { dark: navy, light: '#ffffff' },
      });
    }
  }, [qrUrl]);

  const emergencyName  = student.emergencyContactName  || student.fatherName  || student.motherName || '—';
  const emergencyPhone = student.emergencyContactPhone || student.mobileNumber || '—';
  const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '—';

  return (
    <div style={cardShell}>
      <CardHeader patId={pid} schoolName={schoolName} schoolAddress={schoolAddress} />

      {/* ── BODY ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* Left info column */}
        <div style={{
          flex: 1, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 5,
          background: 'linear-gradient(150deg, #ffffff 0%, #eef2fb 100%)', position: 'relative',
        }}>
          <DotGrid id={`bdg-${pid}`} />
          <div style={{ position: 'absolute', top: 0, left: 0, width: 40, height: 3, background: gold, borderRadius: '0 0 3px 0', zIndex: 2 }} />

          <div>
            {[
              { label: 'Address', val: address, icon: 'pi-map-marker' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 4 }}>
                <div style={{
                  width: 21, height: 21, borderRadius: 6,
                  background: 'rgba(14,34,93,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1,
                }}>
                  <i className={`pi ${r.icon}`} style={{ fontSize: 9, color: navy }} />
                </div>
                <div>
                  <div style={{ fontSize: 8, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{r.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginTop: 1 }}>{r.val}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Emergency */}
          <div style={{
            background: 'rgba(14,34,93,0.03)', border: '1px solid rgba(14,34,93,0.09)',
            borderRadius: 8, padding: '8px 10px',
          }}>
            <div style={{ fontSize: 8, fontWeight: 800, color: navy, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Emergency Contact
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
              <div>
                <div style={{ fontSize: 8, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Name</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginTop: 1 }}>{emergencyName}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 8, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: navy, marginTop: 1 }}>{emergencyPhone}</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{
            background: 'rgba(251,178,31,0.05)', border: '1px solid rgba(251,178,31,0.18)',
            borderRadius: 7, padding: '6px 9px',
          }}>
            {[
              'This ID card is the property of the institute and must be carried at all times within the premises.',
              'It is strictly non-transferable.',
              'If found, please return to: Hindustan Computer Institute.',
            ].map((n, i) => (
              <div key={i} style={{ display: 'flex', gap: 5, marginBottom: i < 2 ? 3 : 0 }}>
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: gold, marginTop: 5.5, flexShrink: 0 }} />
                <div style={{ fontSize: 8, color: '#374151', lineHeight: 1.5 }}>{n}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: QR + signature ── */}
        <div style={{
          width: 116, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 8px',
          background: 'linear-gradient(180deg, #f0f4ff 0%, #e6ecf8 100%)',
          borderLeft: `1px solid rgba(14,34,93,0.1)`, position: 'relative',
        }}>
          <DiagPattern id={`bqr-${pid}`} light />
          <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: `linear-gradient(180deg, transparent, ${gold}, transparent)`, zIndex: 2 }} />

          <div style={{ textAlign: 'center', position: 'relative', zIndex: 3 }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 7, opacity: 0.7 }}>
              Scan to Verify
            </div>
            <div style={{
              width: 84, height: 84, border: `2px solid ${gold}`,
              borderRadius: 10, overflow: 'hidden', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', boxShadow: '0 4px 16px rgba(14,34,93,0.12)',
            }}>
              <canvas ref={qrRef} />
            </div>
            {/* URL label intentionally removed — barcode only */}
          </div>

          {/* Signature */}
          <div style={{
            textAlign: 'center', width: '100%',
            borderTop: `1px solid rgba(14,34,93,0.12)`, paddingTop: 10,
            position: 'relative', zIndex: 3,
          }}>
            <div style={{ height: 24 }} />
            <div style={{ width: '80%', height: 1, background: navy, margin: '0 auto', opacity: 0.3 }} />
            <div style={{ fontSize: 8, fontWeight: 600, color: navy, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 5 }}>
              Issuing Authority
            </div>
          </div>
        </div>
      </div>

      {/* Gold bottom bar */}
      <div style={{ height: 3.5, background: `linear-gradient(90deg, ${navy}, ${gold}, ${navy})`, flexShrink: 0 }} />
    </div>
  );
};

// ─── PRINT HTML BUILDER ───────────────────────────────────────────────────────
const diagPatternSvg = (id: string, light?: boolean) => `
<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none;opacity:${light ? 0.5 : 0.4}">
  <defs>
    <pattern id="${id}" patternUnits="userSpaceOnUse" width="20" height="20" patternTransform="rotate(45)">
      <line x1="0" y1="0" x2="0" y2="20" stroke="${light ? 'rgba(14,34,93,0.07)' : 'rgba(251,178,31,0.14)'}" stroke-width="1.2"/>
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="url(#${id})"/>
</svg>`;

const fmtPrint = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const frontCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  validTill: string,
): string => {
  const vt  = fmtPrint(validTill || s.validTill || batchInfo?.valid_till || null);
  const pid = `pf-${s.student_id}`;
  const initials = getInitials(s.name);
  const enrollId = s.student_custom_id || s.enrollment_id || s.student_id.slice(-6).toUpperCase();

  const rows: [string, string][] = [
    ['Student ID', enrollId],
    ['Father',     s.fatherName || '—'],
    ['Mother',     s.motherName || '—'],
    ['DOB',        fmtPrint(s.dateOfBirth)],
    ['Mobile',     s.mobileNumber || '—'],
    ['Admitted',   fmtPrint(s.date_of_admission)],
    ['Valid Till', vt],
    ...(s.gender   ? [['Gender',   s.gender.charAt(0).toUpperCase() + s.gender.slice(1)] as [string, string]] : []),
    ...(s.category ? [['Category', s.category] as [string, string]] : []),
  ];

  const bloodBadge = s.bloodGroup
    ? `<span class="blood-badge">${s.bloodGroup}</span>`
    : '';

  return `
<div class="card front">
  <div class="hdr">
    ${diagPatternSvg(pid)}
    <div class="gold-bar-top"></div>
    <div class="hdr-left">
      <div class="logo-ring"><img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" class="logo-img" alt=""/></div>
      <div>
        <div class="hdr-name">${schoolName}</div>
        <div class="hdr-addr">${schoolAddress}</div>
      </div>
    </div>
  </div>
  <div class="f-body">
    <div class="photo-col">
      ${diagPatternSvg(`col-${pid}`, true)}
      <div class="photo-wrap">
        ${s.profilePicture?.cdn_url
          ? `<img src="${s.profilePicture.cdn_url}" class="photo-img" alt="${s.name}"/>`
          : `<span class="photo-init">${initials}</span>`}
      </div>
      <div class="photo-name">${s.name}</div>
      <div class="student-badge">Student</div>
      ${bloodBadge}
    </div>
    <div class="info-col">
      <div class="info-grid">
        ${rows.map(([l, v]) => `<div class="info-cell"><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('')}
      </div>
    </div>
  </div>
  <div class="gold-bar-bottom"></div>
</div>`;
};

const backCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  qrUrl: string,
): string => {
  const pid        = `pb-${s.student_id}`;
  const emergName  = s.emergencyContactName  || s.fatherName  || s.motherName || '—';
  const emergPhone = s.emergencyContactPhone || s.mobileNumber || '—';
  const addr       = [s.villageTown, s.district, s.state].filter(Boolean).join(', ') || '—';

  return `
<div class="card back">
  <div class="hdr">
    ${diagPatternSvg(pid)}
    <div class="gold-bar-top"></div>
    <div class="hdr-left">
      <div class="logo-ring"><img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" class="logo-img" alt=""/></div>
      <div>
        <div class="hdr-name">${schoolName}</div>
        <div class="hdr-addr">${schoolAddress}</div>
      </div>
    </div>
  </div>
  <div class="b-body">
    <div class="b-left">
      <div class="c-row"><div class="ic">📍</div><div><div class="lbl">Address</div><div class="val">${addr}</div></div></div>
      <div class="emerg">
        <div class="sec-hd">Emergency Contact</div>
        <div class="emerg-row">
          <div><div class="lbl">Name</div><div class="val">${emergName}</div></div>
          <div class="tr"><div class="lbl">Phone</div><div class="val">${emergPhone}</div></div>
        </div>
      </div>
      <div class="notes">
        <div class="note">Property of the institute. Must be carried in premises.</div>
        <div class="note">Non-transferable.</div>
        <div class="note">If found, return to: Hindustan Computer Institute.</div>
      </div>
    </div>
    <div class="b-right">
      ${diagPatternSvg(`bqr-${pid}`, true)}
      <div style="position:relative;z-index:2;text-align:center">
        <div class="qr-lbl">Scan to Verify</div>
        <div class="qr-box" id="qr-${s.student_id}"></div>
      </div>
      <div class="sig">
        <div class="sig-space"></div>
        <div class="sig-line"></div>
        <div class="sig-lbl">Issuing Authority</div>
      </div>
    </div>
  </div>
  <div class="gold-bar-bottom"></div>
</div>`;
};

export const buildPrintHTML = (
  cards:         Student[],
  batchInfo:     BatchInfo | null,
  schoolName:    string,
  schoolAddress: string,
  websiteUrl:    string,
  validTill:     string,
  branchId?:     string | null,
): string => {
  const qrUrl = branchId
    ? `http://hciindia.in/details/${branchId}`
    : `https://${websiteUrl}`;

  let pages = '';
  for (let i = 0; i < cards.length; i += 4) {
    const slice = cards.slice(i, i + 4);
    pages += `<div class="page">${slice.map(s =>
      `<div class="pair">
        ${frontCardHTML(s, batchInfo, schoolName, schoolAddress, validTill)}
        ${backCardHTML(s, batchInfo, schoolName, schoolAddress, qrUrl)}
      </div>`
    ).join('')}</div>`;
  }

  const css = `
    *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{font-family:'Plus Jakarta Sans',sans-serif;background:#dde4f0}

    .page{width:210mm;margin:0 auto;padding:8mm 10mm;display:flex;flex-direction:column;gap:5mm;page-break-after:always;page-break-inside:avoid}
    .pair{display:flex;flex-direction:row;gap:5mm;align-items:flex-start}

    .card{width:85.6mm;height:54mm;background:#fff !important;border-radius:3.5mm;overflow:hidden;box-shadow:0 2mm 6mm rgba(14,34,93,0.15);position:relative;flex-shrink:0;display:flex;flex-direction:column}

    .gold-bar-top{position:absolute;top:0;left:0;right:0;height:0.75mm;background:linear-gradient(90deg,#fbb21f,#ffdc71,#fbb21f) !important;z-index:5}
    .gold-bar-bottom{height:0.75mm;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important;flex-shrink:0}

    .hdr{background:linear-gradient(100deg,#071640 0%,#0e225d 50%,#1a3a7a 100%) !important;padding:2mm 2.8mm;display:flex;justify-content:space-between;align-items:center;position:relative;overflow:hidden;flex-shrink:0}
    .hdr-left{display:flex;align-items:center;gap:1.8mm;position:relative;z-index:2}
    .logo-ring{width:9mm;height:9mm;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;box-shadow:0 0.5mm 2mm rgba(0,0,0,0.22)}
    .logo-img{width:84%;height:84%;object-fit:contain}
    .hdr-name{font-size:7.5pt;font-weight:800;color:#fff;line-height:1.2}
    .hdr-addr{font-size:5.5pt;color:rgba(255,255,255,0.82);margin-top:0.4mm}

    .f-body{flex:1;display:flex;overflow:hidden;background:linear-gradient(150deg,#fff 0%,#eef2fb 100%) !important;position:relative}
    .photo-col{width:27mm;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.2mm;padding:2.5mm 2mm 2mm;background:linear-gradient(180deg,#f0f4ff 0%,#e6ecf8 100%) !important;border-right:0.3mm solid rgba(14,34,93,0.1);position:relative}
    .photo-wrap{width:18mm;height:20mm;border-radius:1.5mm;overflow:hidden;border:0.65mm solid #fbb21f;box-shadow:0 1.5mm 4mm rgba(14,34,93,0.15);background:#fbb21f !important;display:flex;align-items:center;justify-content:center;flex-shrink:0;position:relative;z-index:2}
    .photo-img{width:100%;height:100%;object-fit:cover;object-position:center top;display:block}
    .photo-init{font-size:18pt;font-weight:800;color:#0e225d}
    .photo-name{font-size:5.5pt;font-weight:800;color:#0e225d;text-align:center;line-height:1.25;word-break:break-word;max-width:25mm;position:relative;z-index:2}
    .student-badge{display:inline-flex;align-items:center;padding:0.6mm 2mm;border-radius:3mm;background:rgba(14,34,93,0.08) !important;border:0.3mm solid rgba(14,34,93,0.15);font-size:4pt;font-weight:700;color:#0e225d;text-transform:uppercase;letter-spacing:0.3pt;position:relative;z-index:2}
    .blood-badge{padding:0.5mm 2mm;border-radius:3mm;background:rgba(220,38,38,0.08) !important;border:0.3mm solid rgba(220,38,38,0.25);font-size:4pt;font-weight:800;color:#dc2626;position:relative;z-index:2}
    .info-col{flex:1;display:flex;flex-direction:column;padding:2.5mm 2.5mm 2mm;min-width:0;position:relative;background-image:radial-gradient(circle,rgba(14,34,93,0.055) 1pt,transparent 1pt);background-size:3.5mm 3.5mm}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.2mm 2.5mm;flex:1;align-content:space-evenly}
    .info-cell{display:flex;flex-direction:column}
    .lbl{font-size:4pt;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.2pt}
    .val{font-size:6pt;font-weight:700;color:#0e225d;margin-top:0.2mm}

    .b-body{flex:1;display:flex;overflow:hidden}
    .b-left{flex:1;padding:2.2mm 2.5mm;display:flex;flex-direction:column;gap:1.5mm;overflow:hidden;position:relative;background:linear-gradient(150deg,#fff 0%,#eef2fb 100%) !important;background-image:radial-gradient(circle,rgba(14,34,93,0.05) 1pt,transparent 1pt);background-size:3.5mm 3.5mm}
    .sec-hd{font-size:4pt;font-weight:800;color:#0e225d;text-transform:uppercase;letter-spacing:0.3pt;margin-bottom:1.2mm}
    .c-row{display:flex;align-items:flex-start;gap:1.5mm;margin-bottom:1mm}
    .ic{width:4.5mm;height:4.5mm;border-radius:1mm;background:rgba(14,34,93,0.07) !important;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:7pt;text-align:center;line-height:4.5mm}
    .emerg{background:rgba(14,34,93,0.03) !important;border-radius:1.2mm;padding:1.5mm 2mm;border:0.3mm solid rgba(14,34,93,0.09)}
    .emerg-row{display:flex;justify-content:space-between;gap:3mm}
    .tr{text-align:right}
    .blood-row{display:flex;align-items:center;gap:1mm;margin-top:1mm;font-size:4pt;color:#dc2626;font-weight:700}
    .notes{background:rgba(251,178,31,0.06) !important;border-radius:1mm;padding:1.2mm 2mm;border:0.3mm solid rgba(251,178,31,0.25)}
    .note{font-size:4pt;color:#374151;line-height:1.6;padding-left:2.5mm;position:relative;margin-bottom:0.3mm}
    .note::before{content:"•";position:absolute;left:0;color:#fbb21f;font-weight:800}

    .b-right{width:24mm;display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:2.5mm 2mm;background:linear-gradient(180deg,#f0f4ff 0%,#e6ecf8 100%) !important;border-left:0.3mm solid rgba(14,34,93,0.1);position:relative}
    .qr-lbl{font-size:4pt;font-weight:700;color:rgba(14,34,93,0.7);text-transform:uppercase;letter-spacing:0.3pt;margin-bottom:1.5mm;text-align:center}
    .qr-box{width:17mm;height:17mm;border:0.6mm solid #fbb21f;border-radius:2mm;overflow:hidden;background:#fff !important;display:flex;align-items:center;justify-content:center;box-shadow:0 1mm 3mm rgba(14,34,93,0.12)}

    .sig{text-align:center;width:100%;border-top:0.3mm solid rgba(14,34,93,0.12);padding-top:1.8mm;position:relative;z-index:2}
    .sig-space{height:6mm}
    .sig-line{width:80%;height:0.4mm;background:rgba(14,34,93,0.3);margin:0.8mm auto}
    .sig-lbl{font-size:4pt;font-weight:600;color:rgba(14,34,93,0.5);text-transform:uppercase;letter-spacing:0.3pt}

    @media print{
      html,body{margin:0;padding:0;background:#fff !important}
      .page{padding:6mm 10mm;gap:4mm;width:210mm}
      .pair{gap:5mm}
      .card{box-shadow:none;border:0.3mm solid #d1d5db}
      @page{size:A4 portrait;margin:0}
    }
  `;

  return `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8"/>
    <title>Student ID Cards</title>
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
    <style>${css}</style>
    </head><body>
    ${pages}
    <script>
      window.onload = function() {
        document.querySelectorAll('[id^="qr-"]').forEach(function(el) {
          new QRCode(el, {
            text: "${qrUrl}",
            width: 64, height: 64,
            colorDark: "#0e225d", colorLight: "#ffffff",
            correctLevel: QRCode.CorrectLevel.M
          });
        });
        setTimeout(function(){ window.print(); }, 1200);
      };
    <\/script></body></html>`;
};